import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { messages } from './messages';
import { emptyObject } from 'ui-components/default-props/empty';
import { EnhancedReturnStatus } from '../../enums/enhanced-return-status';
import { EnhancedReturnsToEntityStatus } from '../../enums/enhanced-return-to-entity-status';
import { EnhancedReturnsAccountStatusEnum } from '../../enums/enhanced-returns-account-status';
import { EnhancedReturnsStoreStatus } from '../../enums/enhanced-returns-store-status';
import { ReturnEntityStatus } from '../../enums/return-entity-status';
import { ReturnEntityType } from '../../enums/return-entity-types';
import { SellerFeatures } from '../../enums/seller-features';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';
import { UserSettings } from '../../enums/user-settings/user-settings';
import historyDefault from '../../history';
import { useIsSellerFeatureEnabled } from '../../hooks/use-is-seller-feature-enabled';
import { useSellerSetting, useEnhancedReturnsAccountStatus, useIsShipstationReturnsEnabled } from '../../hooks/use-seller-setting';
import { useUserSetting } from '../../hooks/use-user-setting';
import { isEmailAddressValid } from '../../services/email-sanitation/isEmailAddressValid';
import { WeightUnit } from '../../enums/weight-unit';
import { SHIPMENTS_RETURNS_PATH, BASE_RETURNS_PATH, FULL_FULFILLMENTS_RETURNS_PATH } from '../../routes/constants';
import Global from '../../store/global-selectors';

export const STORE_ACTIVATION_MODE_BASIC = 'basic';
export const STORE_ACTIVATION_MODE_ADVANCED = 'advanced';

export const STORE_RETURNS_ONBOARDING_TODOS = Object.freeze({
  RETURN_REASONS: 'RETURN_REASONS',
  REFUND_METHODS: 'REFUND_METHODS',
  RETURN_WINDOW: 'RETURN_WINDOW',
  EXCHANGES: 'EXCHANGES',
  SHIPPING: 'SHIPPING',
  FEES: 'FEES',
});

export const BASIC_STORE_ONBOARDING_STEPS = [
  [STORE_RETURNS_ONBOARDING_TODOS.RETURN_REASONS],
  [STORE_RETURNS_ONBOARDING_TODOS.RETURN_WINDOW],
];

export const ADVANCED_STORE_ONBOARDING_STEPS = [
  [STORE_RETURNS_ONBOARDING_TODOS.RETURN_REASONS, STORE_RETURNS_ONBOARDING_TODOS.REFUND_METHODS],
  [STORE_RETURNS_ONBOARDING_TODOS.RETURN_WINDOW],
  [STORE_RETURNS_ONBOARDING_TODOS.EXCHANGES],
  [STORE_RETURNS_ONBOARDING_TODOS.SHIPPING],
  [STORE_RETURNS_ONBOARDING_TODOS.FEES],
];

export const NAVITEM_ENHANCED_RETURNS_ID = 'enhanced';
export const STATUS_PENDING_APPROVAL = 'pending-approval';
export const STATUS_PENDING_PAYMENT = 'pending-payment';
export const STATUS_AWAITING_SHIPMENT = 'awaiting-shipment';
export const STATUS_PROCESSING = 'processing';
export const STATUS_FLAGGED = 'flagged';
export const NEEDS_ATTENTION = 'needs-attention';
export const STATUS_COMPLETED = 'completed';

export const ROUTE_MATCH_PARAM_STATUS_VIEW_ID = ':statusViewId?';
export const ROUTE_MATCH_PARAM_STORE_GUID = ':storeGuid';
export const ROUTE_MATCH_PARAM_RETURN_ID = ':returnId';
// Sentinel store GUID used as a route segment to represent the aggregated all-stores view
export const ALL_STORES_STORE_GUID = 'all-stores';

export const DELEIVERY_STATUS_PAYLOAD_KEY = 'DeliveryStatus';
export const RETURN_SOURCE_PAYLOAD_KEY = 'returnSource';
export const RETURN_SOURCE_LEGACY_PAYLOAD_VALUE = 'Legacy';

// Legacy paths parts
export const NAVITEM_STANDARD_RETURNS_ID = 'standard';
export const STATUS_OUTSTANDING = 'outstanding';
export const STATUS_RECEIVED = 'received';
export const STATUS_VOIDED = 'voided';
export const STATUS_ADVANCED_SEARCH = 'advanced-search';
export const RETURN_SHIPMENT_STATUS_UNKNOWN = 'unknown';
export const RETURN_SHIPMENT_STATUS_IN_TRANSIT = 'in-transit';
export const RETURN_SHIPMENT_STATUS_DELIVERED = 'delivered';
export const RETURN_SHIPMENT_STATUS_VOIDED = 'voided';
export const RETURN_SHIPMENT_STATUS_ALL = 'all';

export const PATH_ENHANCED_RETURNS = `${BASE_RETURNS_PATH}/${NAVITEM_ENHANCED_RETURNS_ID}`;
export const PATH_ENHANCED_RETURNS_ROUTE_MATCH = `${PATH_ENHANCED_RETURNS}/${ROUTE_MATCH_PARAM_STORE_GUID}/${ROUTE_MATCH_PARAM_STATUS_VIEW_ID}`;
export const PATH_ENHANCED_RETURN_DETAILS_ROUTE = `${PATH_ENHANCED_RETURNS_ROUTE_MATCH}/enhanced-return/${ROUTE_MATCH_PARAM_RETURN_ID}`;

// Matches the minimum enforced by the /returngrid/quicksearch backend validator
export const RETURNS_QUICK_SEARCH_MIN_SEARCH_TERM_LENGTH = 3;

export const RETURN_SHIPMENT_STATUS_MAP = {
  [RETURN_SHIPMENT_STATUS_IN_TRANSIT]: 'inTransit',
  [RETURN_SHIPMENT_STATUS_DELIVERED]: 'delivered',
  [RETURN_SHIPMENT_STATUS_UNKNOWN]: 'unknown',
  [RETURN_SHIPMENT_STATUS_VOIDED]: 'voided',
};

export const useIsEnhancedReturnsFeatureEnabled = () => {
  const isSellerFeatureEnabled = useIsSellerFeatureEnabled(SellerFeatures.EnhancedReturns);
  return isSellerFeatureEnabled;
};

export const selectIsEnhancedReturnsFeatureEnabled = (state) => {
  // Selector for non-React usage (epics, etc.)
  // This match the logic of hook useIsEnhancedReturnsFeatureEnabled

  // Ensure features is an array before calling includes
  const features = state?.seller?.features ?? [];
  return features.includes(SellerFeatures.EnhancedReturns);
};

export const useEnhancedReturnsAccountEnabled = () => {
  const enhancedReturnsAccountStatus = useEnhancedReturnsAccountStatus();
  return enhancedReturnsAccountStatus === EnhancedReturnsAccountStatusEnum.Active;
};

export const useIsEnhancedReturnsAccountActiveOrRequested = () => {
  const enhancedReturnsAccountStatus = useEnhancedReturnsAccountStatus();
  const activeOrRequestedStatuses = [EnhancedReturnsAccountStatusEnum.Active, EnhancedReturnsAccountStatusEnum.Requested];
  return activeOrRequestedStatuses.includes(enhancedReturnsAccountStatus);
};

/**
 * Returns the auth token for the ShipStation Returns (SSR) API.
 * It tries to obtain it from these locations:
 * 1. User Settings - for most users, this is the primary source
 * 2. Seller Setting - for drop-in users, we provide this fallback
 * @returns {string | undefined}
 */
export const useEnhancedReturnsAuthToken = () => {
  const userAuthToken = useUserSetting(UserSettings.enhancedReturnsToken.settingName);
  const sellerAuthToken = useSellerSetting(SellerSettings.enhancedReturnsSellerAuthToken);

  return userAuthToken ?? sellerAuthToken;
};

export const useIsUserAbleToUseEnhancedReturns = () => {
  const isEnhancedReturnsFeatureEnabled = useIsEnhancedReturnsFeatureEnabled();
  const isEnhancedReturnsAccountEnabled = useEnhancedReturnsAccountEnabled();
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const authToken = useEnhancedReturnsAuthToken();
  const authTokenPresent = Boolean(authToken);

  if (isShipstationReturnsEnabled) {
    return authTokenPresent;
  }
  return isEnhancedReturnsFeatureEnabled && isEnhancedReturnsAccountEnabled && authTokenPresent;
};

/**
 * A method to check if the current user and store, for the given sales order, support
 * enhanced returns. It depends on three things being true:
 * 1. The store supports enhanced returns (i.e. is one of a limited set of marketplaces)
 * 2. The store has been fully onboarded with SSR
 * 3. The user has a auth token for SSR
 * @param {number} salesOrderId
 * @returns {boolean}
 */
export const useCanCreateEnhancedReturn = (salesOrderId) => {
  const authToken = useEnhancedReturnsAuthToken();
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const salesOrder = useSelector((state) => Global.selectEntitiesSalesOrderById(state, { salesOrderId })) || emptyObject;
  const store =
    useSelector((state) =>
      Global.selectSellerStoreData(state, {
        storeGuid: salesOrder?.store?.storeGuid,
      }),
    ) || emptyObject;
  const isAuthTokenPresent = Boolean(authToken);
  const isEnhancedReturnsEnabled = store?.supportsEnhancedReturns && store?.isStoreSetupCompleteForEnhancedReturns;

  /**
   * ----------------------------|------------------------------|-----------------------------------------
   * isShipstationReturnsEnabled | isEnhancedReturnsEnabled     | Output
   * ----------------------------|------------------------------|-----------------------------------------
   * false                       | false                        | Legacy Returns
   * false                       | true                         | RR Returns (Enhanced Returns - Advanced)
   * true                        | false                        | RR Returns (Enhanced Returns - Basic)
   * true                        | true                         | RR Returns (Enhanced Returns - Advanced)
   * ----------------------------|------------------------------|-----------------------------------------
   */
  return (isShipstationReturnsEnabled || isEnhancedReturnsEnabled) && isAuthTokenPresent;
};

export const useEnhancedReturnRouteMatch = () => {
  const routeMatch = useRouteMatch(`${PATH_ENHANCED_RETURNS_ROUTE_MATCH}`);
  const storeGuid = routeMatch?.params?.storeGuid;
  const statusViewId = routeMatch?.params?.statusViewId;

  return {
    storeGuid,
    statusViewId,
  };
};

export const resetPageQueryParam = (searchQueryParams) => {
  const params = new URLSearchParams(searchQueryParams);
  params.set('page', 1);
  return params.toString();
};

// NOTE: Everything below is in regards to the legacy returns grid, that may exist in 2 different routes
// They also don't leverage ReactRouter route matching because the functionality needs to exist in non-react components (epics) unfortunately
// Hence the regex and includes
export const getReturnsPageBasePath = (currentPagePath, enableShipStationReturns) => {
  const isOnLegacyShippingReturnsPage = currentPagePath.includes(SHIPMENTS_RETURNS_PATH);
  const isOnFulfillmentsReturnsPage = currentPagePath.includes(FULL_FULFILLMENTS_RETURNS_PATH);
  const shouldShowNewReturnsPage = !isOnLegacyShippingReturnsPage && !isOnFulfillmentsReturnsPage && enableShipStationReturns;

  if (isOnFulfillmentsReturnsPage) {
    return FULL_FULFILLMENTS_RETURNS_PATH;
  }

  return shouldShowNewReturnsPage ? `${BASE_RETURNS_PATH}/${NAVITEM_STANDARD_RETURNS_ID}` : SHIPMENTS_RETURNS_PATH;
};

export const getReturnsPageStatusPath = (currentPagePath, enableShipStationReturns) => {
  const basePath = getReturnsPageBasePath(currentPagePath, enableShipStationReturns);
  // Gets the status from the path but ignores any additional path segments like Order/Returns Details

  const match = currentPagePath.match(`${basePath}/([a-zA-Z-]*)`);

  if (match) {
    return match[1] || RETURN_SHIPMENT_STATUS_UNKNOWN;
  }

  return RETURN_SHIPMENT_STATUS_UNKNOWN;
};

export const getIsReturnsPage = (enableShipStationReturns) => {
  const { pathname } = historyDefault.location;
  const basePath = getReturnsPageBasePath(pathname, enableShipStationReturns);

  return pathname.includes(basePath);
};

export const getPreviousAndNextFulfillmentIdsFromReturnsGrid = (state, fulfillmentId, enableShipStationReturns) => {
  const defaultFulfillmentIds = {
    nextFulfillmentId: null,
    previousFulfillmentId: null,
  };

  if (!getIsReturnsPage(enableShipStationReturns)) {
    return defaultFulfillmentIds;
  }

  const fulfillments = Global.selectReturnsGridReturns(state);
  const fulfillmentIndex = fulfillments.findIndex((record) => record.fulfillmentId === fulfillmentId);

  if (fulfillmentIndex === -1) {
    return defaultFulfillmentIds;
  }

  const nextFulfillmentId = fulfillmentIndex < fulfillments.length - 1 ? fulfillments[fulfillmentIndex + 1].fulfillmentId : null;
  const previousFulfillmentId = fulfillmentIndex > 0 ? fulfillments[fulfillmentIndex - 1].fulfillmentId : null;

  return {
    ...defaultFulfillmentIds,
    nextFulfillmentId,
    previousFulfillmentId,
  };
};

export const showReturnEmailFailedNotification = (errorMessage) => {
  NotificationService.showNotification({
    title: <FormattedMessage {...messages.failedLabelTitle} />,
    content: errorMessage,
    notificationType: NOTIFICATION_TYPE_ERROR,
  });
};

export const isCancelled = (requestData = {}) => EnhancedReturnStatus.Cancelled.key === requestData?.status;

export const isCompleted = (requestData = {}) =>
  [EnhancedReturnStatus.Processed.key, EnhancedReturnStatus.Cancelled.key].includes(requestData?.status);

export const isApprovable = (requestData = {}) => {
  const isActionableStatus = requestData.status === EnhancedReturnStatus.PendingAtAdmin.key;
  return isActionableStatus && !requestData.onHold;
};

export const isPendingPayment = (requestData = {}) => requestData?.status === EnhancedReturnStatus.PendingPaymentCompletion.key;

export const isAwaitingShipment = (requestData = {}) => {
  return requestData?.status === EnhancedReturnStatus.AwaitingShipment.key && !requestData?.shippingInfo?.shipmentLabel;
};

export const isMarketplaceAuthenticationInFlight = (status) => {
  return [
    EnhancedReturnsStoreStatus.AuthenticationInitiated,
    EnhancedReturnsStoreStatus.Pending,
    EnhancedReturnsStoreStatus.Processing,
  ].includes(status);
};

export const isStoreSetupInProgress = (status) => {
  return (
    isMarketplaceAuthenticationInFlight(status) ||
    [EnhancedReturnsStoreStatus.DefaultCarrierSelected, EnhancedReturnsStoreStatus.Authenticated].includes(status)
  );
};

export const returnEntityDisplayStatus = (statusKey = '') => {
  const status = Object.values(ReturnEntityStatus).find((s) => s.key === statusKey);
  return status?.translation ?? statusKey;
};

export const isActionableReturnEntity = (requestData = {}, returnExchange = {}) => {
  const isRequestApprovable = isApprovable(requestData);
  const isActionableType = returnExchange.entityType !== ReturnEntityType.Purchase;
  const relatedEntityStatuses = EnhancedReturnsToEntityStatus[requestData?.status] || [];
  return isRequestApprovable && isActionableType && relatedEntityStatuses.includes(returnExchange.status);
};

export const getDefaultSelectedData = (requestData = {}) => {
  return (requestData?.returnExchanges ?? []).reduce((acc, returnExchange) => {
    const isActionable = isActionableReturnEntity(requestData, returnExchange);
    const isPurchase = returnExchange?.entityType === ReturnEntityType.Purchase;
    const isSuccessStatus = [
      ReturnEntityStatus.RefundSuccess.key,
      ReturnEntityStatus.ExchangeSuccess.key,
      ReturnEntityStatus.AwaitingPaymentCompletion.key,
    ].includes(returnExchange?.status);
    if (isPurchase) return acc;
    return {
      ...acc,
      [returnExchange.id]: {
        approve: isActionable ? true : isSuccessStatus,
        restock: returnExchange?.inventoryOptions?.restock ?? true,
      },
    };
  }, {});
};

export const getRequestDetailsWithSortedReturnExchanges = (requestData = {}) => {
  const requestDetails = { ...requestData };
  // Sort so that actionable items appear first in the list
  (requestData?.returnExchanges ?? []).sort((returnExchangeA, returnExchangeB) => {
    const isActionableA = isActionableReturnEntity(requestDetails, returnExchangeA);
    const isActionableB = isActionableReturnEntity(requestDetails, returnExchangeB);
    return isActionableB - isActionableA;
  });

  return requestDetails;
};

/**
 * @param {import('../../orders/order-details/enhanced-return/enhanced-return-types').ReturnDetails} requestData
 * @returns {string} A comma-separated string of unique, non-empty email addresses associated with the return request, including both the customer's email and any additional emails specified in the request data.
 */
export const getCommaSeparatedEmailsFromRequestDetails = (requestData = {}) => {
  const customerEmail = requestData?.shippingAddress?.email ?? '';
  const hasEmailTo = Array.isArray(requestData?.emailTo) && requestData.emailTo.length > 0;
  const emails = hasEmailTo ? requestData.emailTo : [customerEmail];
  return Array.from(new Set(emails))
    .map((email) => email.trim())
    .filter((email) => isEmailAddressValid(email))
    .join(',');
};

/**
 * @param {string} insuranceProvider
 * @returns {boolean}
 */
export const isReturnInsured = (insuranceProvider) => {
  const normalizedInsuranceProvider = insuranceProvider.trim().toLocaleLowerCase();
  return !!normalizedInsuranceProvider && normalizedInsuranceProvider !== 'none';
};

export const VALID_RETURN_WEIGHT_UNITS = [WeightUnit.Ounces, WeightUnit.Pounds, WeightUnit.Grams, WeightUnit.Kilograms];

/**
 * @param {typeof VALID_RETURN_WEIGHT_UNITS[number]} weightUnit
 * @returns {boolean}
 */
export const isValidReturnWeightUnit = (weightUnit) => VALID_RETURN_WEIGHT_UNITS.includes(weightUnit);

/**
 *
 * @param {{ length: number, width: number, height: number}} dimensions
 * @param {(value: number) => string} formatNumber
 * @returns {string}
 */
export const generateReturnShipmentSizeString = (dimensions, formatNumber) => {
  return [dimensions.length, dimensions.width, dimensions.height]
    .filter(Boolean)
    .map((val) => formatNumber(val))
    .join(' x ');
};
